import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    Box,
    IconButton,
    Typography,
    Menu,
    MenuItem,
    Avatar,
    Divider,
    ListItemIcon,
    ListItemText,
    Tooltip,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Logout as LogoutIcon,
    AccountCircle as AccountCircleIcon,
    Language as LanguageIcon,
    LightMode as LightModeIcon,
    DarkMode as DarkModeIcon,
    Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import usePermission from '../../security/hook/usePermission';
import { useSecurityContext } from '../../security/hook/useSecurityContext';
import { useLogoutUserAPI } from '../../api/auth/useLogoutUserAPI';
import { useThemeMode } from '../../context/ThemeContext';
import Logo from '../../static/app_logo.png';
import './header.css';

const Header: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { mode, toggleTheme } = useThemeMode();

    const { user, resetUserData } = useSecurityContext();
    const { logoutUser } = useLogoutUserAPI();
    const { userIsAuthenticated } = usePermission();

    // State for menus
    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
    const [anchorElLanguage, setAnchorElLanguage] = useState<null | HTMLElement>(null);
    const [currentLanguage, setCurrentLanguage] = useState<string>('EN');

    const userName = user?.user?.firstName && user?.user?.lastName
        ? `${user.user.firstName} ${user.user.lastName}`
        : 'User';

    const userInitials = userName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleOpenLanguageMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElLanguage(event.currentTarget);
    };

    const handleCloseLanguageMenu = () => {
        setAnchorElLanguage(null);
    };

    const handleLanguageChange = (lang: string) => {
        setCurrentLanguage(lang);
        handleCloseLanguageMenu();
        // TODO: Implement actual i18n language change with i18next
        console.log('Language changed to:', lang);
    };

    const handleLogout = async () => {
        handleCloseUserMenu();
        await logoutUser();
        resetUserData();
        navigate('/login');
    };

    const handleProfile = () => {
        handleCloseUserMenu();
        // TODO: Navigate to profile page
        console.log('Navigate to profile');
    };

    const handleSettings = () => {
        handleCloseUserMenu();
        // TODO: Navigate to settings page
        console.log('Navigate to settings');
    };

    if (!userIsAuthenticated()) {
        return null;
    }

    return (
        <AppBar
            position="sticky"
            elevation={2}
            sx={{
                backgroundColor: 'background.paper',
                color: 'text.primary',
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Toolbar sx={{ gap: 2 }}>
                <Box
                    component="img"
                    src={Logo}
                    alt="iAuto Logo"
                    className="iauto-header-logo"
                    sx={{
                        height: { xs: 30, sm: 40 },
                        width: 'auto',
                        cursor: 'pointer',
                    }}
                    onClick={() => navigate('/')}
                />

                <Box sx={{ flexGrow: 1 }} />

                {!isMobile && (
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 500,
                            color: 'text.secondary',
                        }}
                    >
                        {userName}
                    </Typography>
                )}

                <Tooltip title={mode === 'dark' ? 'Light Mode' : 'Dark Mode'}>
                    <IconButton
                        onClick={toggleTheme}
                        color="primary"
                        className="iauto-header-icon-button"
                        sx={{
                            transition: 'transform 0.3s ease',
                            '&:hover': {
                                transform: 'rotate(180deg)',
                            },
                        }}
                    >
                        {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                    </IconButton>
                </Tooltip>

                {/* Language Selector */}
                <Tooltip title="Select Language">
                    <IconButton
                        onClick={handleOpenLanguageMenu}
                        color="primary"
                        className="iauto-header-icon-button"
                    >
                        <LanguageIcon />
                    </IconButton>
                </Tooltip>
                <Menu
                    anchorEl={anchorElLanguage}
                    open={Boolean(anchorElLanguage)}
                    onClose={handleCloseLanguageMenu}
                    className="iauto-header-menu"
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    slotProps={{
                        paper: {
                            elevation: 8,
                            sx: {
                                minWidth: 150,
                                mt: 1.5,
                            },
                        },
                    }}
                >
                    <MenuItem
                        onClick={() => handleLanguageChange('EN')}
                        selected={currentLanguage === 'EN'}
                    >
                        🇬🇧 English
                    </MenuItem>
                    <MenuItem
                        onClick={() => handleLanguageChange('RU')}
                        selected={currentLanguage === 'RU'}
                    >
                        🇷🇺 Русский
                    </MenuItem>
                    <MenuItem
                        onClick={() => handleLanguageChange('RO')}
                        selected={currentLanguage === 'RO'}
                    >
                        🇷🇴 Română
                    </MenuItem>
                </Menu>

                {/* User Avatar & Menu */}
                <Tooltip title="Account">
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                        <Avatar
                            className="iauto-header-avatar"
                            sx={{
                                bgcolor: 'primary.main',
                                width: 40,
                                height: 40,
                                fontWeight: 600,
                                fontSize: '0.875rem',
                            }}
                        >
                            {userInitials}
                        </Avatar>
                    </IconButton>
                </Tooltip>
                <Menu
                    anchorEl={anchorElUser}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                    className="iauto-header-menu"
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    slotProps={{
                        paper: {
                            elevation: 8,
                            sx: {
                                minWidth: 220,
                                mt: 1.5,
                                borderRadius: 2,
                            },
                        },
                    }}
                >
                    {/* User Info Header */}
                    <Box sx={{ px: 2, py: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {userName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {user?.user?.role || 'Admin'}
                        </Typography>
                    </Box>
                    <Divider />

                    {/* Menu Items */}
                    <MenuItem onClick={handleProfile}>
                        <ListItemIcon>
                            <AccountCircleIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>My Profile</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleSettings}>
                        <ListItemIcon>
                            <SettingsIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Settings</ListItemText>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout}>
                        <ListItemIcon>
                            <LogoutIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText>
                            <Typography color="error">Logout</Typography>
                        </ListItemText>
                    </MenuItem>
                </Menu>
            </Toolbar>
        </AppBar>
    );
};

export default Header;